<template>
  <v-card class="step-tab-panel" flat>
    <ProgressBar v-if="isLoading" />
    <v-container v-else-if="product" class="pb-0 pt-2" fluid>
      <!-- Header-->
      <v-row align="center" no-gutters class="ma-0 mb-2">
        <v-col cols="7" class="d-flex align-center tooltip-text">
          <!-- Product alerts icon -->
          <IconRenderer class="mr-2" :params="productAlertParams" />
          <!-- Copied decisions icon -->
          <IconRenderer class="mr-2" :params="copyDecisionsParams" />

          <v-tooltip v-if="fullProductTitle !== displayedProductTitle" location="bottom">
            <template #activator="{ props }">
              <h1 v-bind="props" id="detailViewProductNameHeader">
                {{ displayedProductTitle }}
              </h1>
            </template>
            <span class="tooltip-text"> {{ fullProductTitle }}</span>
          </v-tooltip>
          <h1 v-else id="detailViewProductNameHeader">
            {{ displayedProductTitle }}
          </h1>
          <v-chip
            v-if="hasMarkedAsCompleted"
            class="ml-2"
            color="success"
            density="compact"
            variant="flat"
            prepend-icon="mdi-checkbox-marked-circle"
          >
            {{ tkey('️completed') }}
          </v-chip>
        </v-col>

        <v-col cols="12" md="5" class="d-flex align-center justify-end flex-wrap ga-2">
          <v-btn
            class="outlined-btn mr-2"
            variant="flat"
            secondary
            :disabled="isClearFiltersDisabled"
            @click="clearFilters"
          >
            {{ $t('actions.clearAllFilters') }}
          </v-btn>
          <RtlsSearch
            v-model="searchString"
            :placeholder="tkey('searchPlaceholder')"
            class="mr-3"
            @update:model-value="onFilterChanged"
          />
          <BaseToggle
            v-model="currentlySelectedSisterProductsView"
            :left-toggle="visibleSisterProductsToggle.leftToggle"
            :right-toggle="visibleSisterProductsToggle.rightToggle"
            @input="onViewToggleChange"
          />
          <v-btn
            id="goBackBtn"
            icon
            size="small"
            variant="flat"
            class="ml-2"
            :disabled="isGridLoading"
            @click="goBack"
          >
            <v-icon> mdi-close-circle </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <!-- Header-->
      <v-row v-if="hasDataErrors" no-gutters>
        <div>
          <v-alert
            v-if="doesNotHaveSisProductsSelected"
            id="noSisterProductsSelectedWarning"
            type="error"
            variant="outlined"
            >{{ tkey('mustHaveSisterProductsWarning') }}</v-alert
          >
        </div>
      </v-row>
    </v-container>
    <!--Sister products selection-->
    <div class="h-100 flex-grow-1">
      <AgGridVue
        data-testid="grid"
        class="w-100 h-100"
        :loading="isGridLoading"
        :row-data="gridRowData"
        :grid-options="gridOptions"
        :pinned-top-row-data="pinnedRows"
        :does-external-filter-pass="doesExternalFilterPass"
        @cell-value-changed="handleCellEdit"
        @grid-ready="onGridReady"
        @drag-stopped="saveColumnState"
        @column-visible="saveColumnState"
      />
    </div>
    <!-- footer-->
    <PageActions
      :has-data-changes="hasDataChanges"
      :has-data-errors="!isLoading && hasDataErrors"
      :is-discard-enabled="hasDataChanges && !isGridLoading"
      :is-custom-export="true"
      :show-export="true"
      :save-loading="isSaving"
      :show-save-button="false"
      @discard="discardChanges"
      @export="onExport"
    >
      <template #right-left-btns>
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-btn
              id="backToLastRowBtn"
              v-bind="props"
              variant="outlined"
              :disabled="isGridLoading"
              prepend-icon="mdi-arrow-left"
              class="mr-4"
              @click="move(-1)"
            />
          </template>
          <span class="tooltip-text">{{ $t('actions.backToLastRow') }}</span>
        </v-tooltip>

        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-btn
              id="proceedToNextBtn"
              v-bind="props"
              :disabled="isGridLoading"
              variant="outlined"
              append-icon="mdi-arrow-right"
              @click="move(1)"
            />
          </template>
          <span class="tooltip-text">{{ $t('actions.proceedToNext') }}</span>
        </v-tooltip>
      </template>

      <template #right-end-btns>
        <v-btn
          id="markAsCompleteBtn"
          action
          variant="flat"
          class="mr-4"
          :disabled="isEditingDisabled || doesNotHaveSisProductsSelected || isGridLoading"
          @click="handleSaveAndProceed()"
        >
          {{ $t('actions.markAsComplete') }}
        </v-btn>
      </template>
    </PageActions>

    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="productModellingDetail"
      save-btn-text="proceedWithDeleting"
      proceed-btn-text="proceedWithoutDeleting"
      warning-message="dependencyTree.productModellingWarning"
      able-to-proceed-without-deleting
      @close="closeDependencyTreeModal"
      @proceed="saveAndProceed()"
      @commit="handleSaveAndProceed(true)"
    />

    <UnsavedDataModal
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import to from 'await-to-js';
// utilities
import cannGroupUtils from '@/utils/cann-groups';
import numberUtils from '@/utils/number-format-utils';
import { mapActions, mapState, mapGetters } from 'pinia';
import {
  camelCase,
  cloneDeep,
  every,
  get,
  isArray,
  isEmpty,
  keyBy,
  map,
  merge,
  omit,
  partition,
  pick,
  reject,
  size,
  sortBy,
  toUpper,
  findIndex,
  filter,
  find,
  values,
  assign,
  setWith,
  has,
  isNil,
  mapValues,
  reduce,
  round,
  orderBy,
  forEach,
  truncate,
} from 'lodash';
import dayjs from 'dayjs';
import { onKeyStroke } from '@vueuse/core';

import agGridUtils from '@/utils/ag-grid-utils';
import { jobApi } from '@/helpers';
import colors from '@/plugins/ow-colors';
// stores
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useSnackbarStore } from '@/stores/snackbar';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
// composables
import { useRouter, useRoute } from 'vue-router';
import { usePermission } from '@/composables/use-permission';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFormatNumber } from '@/composables/use-format-number';
import { useFeature } from '@/composables/use-feature';
import { useProductIndicators } from '@/composables/product-indicators';
import { setGridApiForTesting } from '@/composables/use-grid-api-for-testing';
// enums
import modellingTypeOptions from '@enums/product-modelling-types';
import cdtTypes from '@enums/scenario-cdt-types';

import gridPreferences from '@/utils/grid-preferences';
import productUtils from '@/utils/product-utils';
import modellingUtils from '@/utils/modelling-utils';
import { useProductModellingGridHelpers } from './composables/product-modelling-grid-helpers';
import { useProductModelling } from './composables/product-modelling';

const DETAIL_COLUMN_STATE_PREFIX = 'productModellingDetailColumnState';
const DETAIL_COLUMN_STATE_VERSION = 1;

const localizationKey = 'productModellingPage';

export default {
  beforeRouteLeave(routeTo, from, next) {
    this.beforeRouteLeave(routeTo, from, next);
  },

  beforeRouteUpdate(routeTo, from, next) {
    // Same functionality applies to both routeLeave and routeUpdate
    this.beforeRouteLeave(routeTo, from, next);
  },

  setup() {
    const router = useRouter();
    const route = useRoute();
    const { canEditModellingPage } = usePermission();
    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();
    const { tkey, t } = useInternationalization(localizationKey);

    const { formatMargin, formatNumberWithOneDecimal } = useFormatNumber();

    const {
      hasProductsExistingFromNewInTemplateEnabled,
      hasClientProductKeyColumnEnabled,
      modellingDetailPageAllowedColumns,
    } = useFeature();

    const { fetchData, isProductEstablished } = useProductModelling();
    const { getProductType, getProductModellingTag } = useProductIndicators();
    const { salesConfidenceScoreRenderer, unsetCurrentStateDataDiff } =
      useProductModellingGridHelpers();

    return {
      t,
      tkey,
      canEditModellingPage,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      formatMargin,
      formatNumberWithOneDecimal,
      hasProductsExistingFromNewInTemplateEnabled,
      fetchData,
      isProductEstablished,
      getProductType,
      getProductModellingTag,
      hasClientProductKeyColumnEnabled,
      salesConfidenceScoreRenderer,
      modellingDetailPageAllowedColumns,
      unsetCurrentStateDataDiff,
      // routing
      router,
      route,
      get,
    };
  },

  data() {
    return {
      isLoading: false, // controls overall page loading
      savedState: {}, // original saved state from mongo
      product: null, // this is the product being modelled for the pinned row
      allEligibleProducts: [],
      allIneligibleProducts: [],
      leafCannGroups: {},
      clusteredProductData: [],
      productsRequiringModelling: [],
      currentlySelectedSisterProductsView: 'eligibleOnly',
      productsRoSPercentileMap: {},
      visibleSisterProductsToggle: {
        leftToggle: {
          value: 'eligibleOnly',
          translation: this.tkey('eligibleSisterProducts'),
        },
        rightToggle: {
          value: 'allProducts',
          translation: this.tkey('allProducts'),
        },
      },
      // Editing helpers
      isSaving: false,
      currentStateDiff: {},
      // Dependency tree objects
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,

      // Ag-Grid data
      filterModel: null,
      searchString: '',
      gridApi: null,
      gridRowData: [],
      pinnedRows: [],
      isGridLoading: false,
      gridOptions: {
        enableRangeSelection: true,
        tooltipShowDelay: 0,
        getRowId(row) {
          const { data } = row;
          return data._id;
        },
        getRowStyle: params => {
          if (params.node.rowPinned === 'top') {
            return { backgroundColor: colors.assortmentBlueLight };
          }
        },
        getRowClass: params => {
          const isCurrentProductRendered = params.data.productKey === this.product.productKey;
          if (this.canRowBeSelected(params.node) || isCurrentProductRendered) return;

          return 'text-disabled';
        },
        // enables external view for eligible/ineligible products to be shown
        isExternalFilterPresent: () => true,
        onFilterChanged: () => {
          this.filterModel = this.gridApi.getFilterModel();
        },
        // Enables the tool panel on RHS of the grid
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              minWidth: 225,
              maxWidth: 225,
              width: 225,
              toolPanelParams: {
                suppressPivotMode: true,
                suppressPivots: true,
                suppressRowGroups: true,
                suppressValues: true,
              },
            },
          ],
        },
        columnTypes: { numericAlignment: agGridUtils.columnTypes.numericAlignment },
      },
      // list of all default columns
      initialHeaders: [
        {
          headerName: this.tkey('selected'),
          headerTooltip: this.tkey('headerTooltips.sisterProductSelection'),
          field: 'isSelectedSisterProduct',
          colId: 'isSelectedSisterProduct',
          pinned: 'left',
          cellDataType: 'boolean',
          suppressHeaderMenuButton: true,
          resizable: false,
          lockPosition: true,
          maxWidth: 30,
          editable: params =>
            params.data.productKey !== this.product.productKey &&
            this.canRowBeSelected(params.node),
          headerClass: 'editable-column-header',
          menuTabs: [],
          cellClassRules: {
            'diff-background': this.hasDiff,
            'd-none': params => !this.canRowBeSelected(params.node),
          },
        },
        {
          headerName: this.tkey('type'),
          headerTooltip: this.tkey('headerTooltips.productTag'),
          field: 'productTag',
          colId: 'productTag',
          pinned: 'left',
          maxWidth: 50,
          valueGetter: params => {
            // Check if this is the pinned row
            const isCurrentlyProductBeingModelled =
              params.data.productKey === this.product.productKey;

            if (isCurrentlyProductBeingModelled) {
              const productTag = this.getProductModellingTag(this.product);
              if (!productTag) return '';

              return toUpper(this.$t(`assortmentCanvasPage.productTag.${productTag}`));
            }

            const currentSisProductsSelected = merge(
              {},
              pick(this.savedState, ['sisterProducts']),
              pick(this.currentStateDiff, ['sisterProducts'])
            ).sisterProducts[params.data.productKey];

            if (!currentSisProductsSelected) return;

            const productType = this.getProductType(currentSisProductsSelected);
            return productType.translation === modellingTypeOptions.existingProduct.translation
              ? ''
              : this.tkey(productType.translation, localizationKey);
          },
        },
        {
          field: 'productKeyDisplay',
          colId: 'productKey',
          headerName: this.tkey('productKeyDisplay'),
          width: 140,
          pinned: 'left',
          filter: 'agTextColumnFilter',
          filterParams: agGridUtils.filters.standardStringFilter,
        },
        {
          field: 'itemDescription',
          colId: 'itemDescription',
          headerName: this.tkey('productDescription'),
          pinned: 'left',
          filter: 'agTextColumnFilter',
          filterParams: agGridUtils.filters.standardStringFilter,
        },
        {
          field: 'clientProductKey',
          colId: 'clientProductKey',
          headerName: this.t('scenarioInputPage.attributes.clientProductKey'),
          width: 120,
          pinned: 'left',
          filter: 'agTextColumnFilter',
          filterParams: agGridUtils.filters.standardStringFilter,
          hide: !this.hasClientProductKeyColumnEnabled,
        },
        {
          colId: 'relevanceScore',
          field: 'relevanceScore',
          headerTooltip: this.tkey('headerTooltips.relevanceScore'),
          headerName: this.tkey('relevanceScore'),
          width: 175,
          valueGetter: this.getRelevanceScore,
          type: 'numericAlignment',
        },
        {
          colId: 'combinedUnitAndMeasure',
          field: 'combinedUnitAndMeasure',
          headerName: this.tkey('combinedContentAndUnit'),
          width: 175,
          type: 'numericAlignment',
          valueGetter(params) {
            return (params.data.contentValue || '') + (params.data.contentUnitOfMeasure || '');
          },
        },
        {
          colId: 'theoreticalMargin',
          field: 'theoreticalMargin',
          headerName: this.$t('lookup.margin'),
          type: ['numericColumnCustom', 'numericAlignment'],
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          valueFormatter: ({ value }) => this.formatNumberWithOneDecimal(value, true),
          // Convert a value stored like 0.00525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
          filterValueGetter: params => {
            // multiply by 100 because, margin would store 5.25% as 0.00525 but the 'numberWithOneDecimal'
            // format won't automatically multiply by 100.
            const margin = this.formatNumberWithOneDecimal(
              get(params.data, params.colDef.colId) * 100
            );
            return numberUtils.stringToNumberIfNumeric(margin);
          },
          cellClassRules: { 'diff-background': this.hasDiff },
          width: 101,
        },
        {
          colId: 'theoreticalPrice',
          field: 'theoreticalPrice',
          headerName: `${this.$t('scenarioInputPage.attributes.price')} (${this.t(
            'lookup.currency'
          )})`,
          filter: 'agNumberColumnFilter',
          type: 'numericAlignment',
          valueFormatter: params => agGridUtils.formatters.roundFormatter(params),
          valueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
          filterValueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
          cellClassRules: { 'diff-background': this.hasDiff },
          width: 111,
        },
      ],
      // list of columns to be split on unc+cluster level
      clusterableColumns: {
        rateOfSale: {
          headerName: this.tkey('expected'),
          headerTooltip: this.tkey('headerTooltips.measuredModelledRoS'),
          width: 175,
          field: 'rateOfSale',
          colId: 'rateOfSale',
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          type: 'numericAlignment',
          valueFormatter: params => agGridUtils.formatters.roundFormatter(params),
          valueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
          filterValueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
          cellClassRules: { 'diff-background': this.hasDiff },
        },
        rosPercentile: {
          headerName: this.tkey('rosPercentile'),
          headerTooltip: this.tkey('headerTooltips.rosPercentile'),
          width: 130,
          field: 'rosPercentile',
          colId: 'rosPercentile',
          type: ['numericColumnCustom', 'numericAlignment'],
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          valueFormatter: params => agGridUtils.formatters.percentageFormatter(params.value, 2),
          editable: false,
          cellClassRules: { 'diff-background': this.hasDiff },
        },
        salesConfidenceScorePerformancePeriod: {
          field: 'salesConfidenceScorePerformancePeriod',
          colId: 'salesConfidenceScorePerformancePeriod',
          headerName: this.tkey('salesConfidenceScore'),
          headerTooltip: this.tkey('headerTooltips.salesConfidence'),
          width: 80,
          filter: false,
          cellRenderer: params => this.salesConfidenceScoreRenderer(params),
        },
        margin: {
          colId: 'margin',
          field: 'margin',
          // this column is hidden by default because the user not necessarily needs to see this column
          // but we need the column to save the new calculated value for margin/price
          hide: true,
          headerName: this.tkey('marginIncWasteMarkdown'),
          headerTooltip: this.tkey('headerTooltips.marginIncWasteMarkdown'),
          type: ['numericColumnCustom', 'numericAlignment'],
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          valueFormatter: ({ value }) => this.formatNumberWithOneDecimal(value, true),
          // Convert a value stored like 0.00525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
          filterValueGetter: params => {
            // multiply by 100 because, margin would store 5.25% as 0.00525 but the 'numberWithOneDecimal'
            // format won't automatically multiply by 100.
            const margin = this.formatNumberWithOneDecimal(
              get(params.data, params.colDef.colId) * 100
            );
            return numberUtils.stringToNumberIfNumeric(margin);
          },
          cellClassRules: { 'diff-background': this.hasDiff },
          width: 147,
        },
        price: {
          colId: 'price',
          field: 'price',
          // this column is hidden by default because the user not necessarily needs to see this column
          // but we need the column to save the new calculated value for margin/price
          hide: true,
          headerName: `${this.tkey('priceIncMarkdown')} (${this.t('lookup.currency')})`,
          headerTooltip: this.tkey('headerTooltips.priceIncMarkdown'),
          filter: 'agNumberColumnFilter',
          type: 'numericAlignment',
          valueFormatter: params => agGridUtils.formatters.roundFormatter(params),
          valueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
          filterValueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
          cellClassRules: { 'diff-background': this.hasDiff },
          width: 150,
        },
        shrinkToSalesRatio: {
          headerName: this.tkey('shrinkToSalesRatio'),
          headerTooltip: this.tkey('headerTooltips.shrinkToSalesRatio'),
          colId: 'shrinkToSalesRatio',
          field: 'shrinkToSalesRatio',
          type: ['numericColumnCustom', 'numericAlignment'],
          filter: 'agNumberColumnFilter',
          width: 120,
          valueFormatter: ({ value }) => {
            if (!value) return '-';
            return this.formatNumberWithOneDecimal(value, true);
          },
          filterValueGetter: params => {
            // Return null for falsy (including 0) values to ensure the "blank" and "not blank" filters work correctly
            // for cells which display a dash when the value is 0.
            const value = get(params.data, params.colDef.colId);
            if (!value) return null;
            // multiply by 100 because, unlike waste, markdown would store 5.25% as 0.00525 but the 'numberWithOneDecimal'
            // format won't automatically multiply by 100.
            const margin = this.formatNumberWithOneDecimal(value * 100);
            return numberUtils.stringToNumberIfNumeric(margin);
          },
          cellClassRules: { 'diff-background': this.hasDiff },
        },
        totalMarkdownAssortmentPercentage: {
          headerName: this.tkey('totalMarkdownAssortmentPercentage'),
          headerTooltip: this.tkey('headerTooltips.totalMarkdownAssortmentPercentage'),
          colId: 'totalMarkdownAssortmentPercentage',
          field: 'totalMarkdownAssortmentPercentage',
          type: ['numericColumnCustom', 'numericAlignment'],
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          width: 140,
          valueFormatter: ({ value }) => {
            if (!value) return '-';
            return this.formatNumberWithOneDecimal(value, true);
          },
          // Convert a value stored like 0.00525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
          filterValueGetter: params => {
            // Return null for falsy (including 0) values to ensure the "blank" and "not blank" filters work correctly
            // for cells which display a dash when the value is 0.
            const value = get(params.data, params.colDef.colId);
            if (!value) return null;
            // multiply by 100 because, unlike waste, markdown would store 5.25% as 0.00525 but the 'numberWithOneDecimal'
            // format won't automatically multiply by 100.
            const margin = this.formatNumberWithOneDecimal(value * 100);
            return numberUtils.stringToNumberIfNumeric(margin);
          },
          cellClassRules: { 'diff-background': this.hasDiff },
        },
        wastePercentageAssortment: {
          headerName: this.tkey('wastePercentageAssortment'),
          headerTooltip: this.tkey('headerTooltips.wastePercentageAssortment'),
          colId: 'wastePercentageAssortment',
          field: 'wastePercentageAssortment',
          type: ['numericColumnCustom', 'numericAlignment'],
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          width: 120,
          valueFormatter: ({ value }) => {
            if (!value) return '-';
            // divide by 100 because, unlike markdown, waste would store 5.25% as 0.525 but the 'percentagesWithOneDecimal'
            // format will automatically multiply by 100.
            return this.formatNumberWithOneDecimal(value / 100, true);
          },
          // Convert a value stored like 0.525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
          filterValueGetter: params => {
            // Return null for falsy (including 0) values to ensure the "blank" and "not blank" filters work correctly
            // for cells which display a dash when the value is 0.
            const value = get(params.data, params.colDef.colId);
            if (!value) return null;

            const margin = this.formatNumberWithOneDecimal(value);
            return numberUtils.stringToNumberIfNumeric(margin);
          },
          cellClassRules: { 'diff-background': this.hasDiff },
        },
      },
      historicalPerformanceColumns: [
        {
          headerName: `${this.tkey('totalSales')} ${this.t('lookup.currency')}`,
          headerTooltip: this.tkey('headerTooltips.totalSalesAssortment'),
          field: 'totalSalesAssortment',
          colId: 'totalSalesAssortment',
          width: 130,
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          type: 'numericAlignment',
          valueFormatter: params =>
            agGridUtils.formatters.roundFormatter(params, { format: 'number' }),
          valueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, {
              returnAsNumber: true,
              format: 'number',
            }),
          filterValueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, {
              returnAsNumber: true,
              format: 'number',
            }),
        },
        {
          headerName: this.tkey('totalVolume'),
          headerTooltip: this.tkey('headerTooltips.totalVolumeAssortment'),
          field: 'totalVolumeAssortment',
          colId: 'totalVolumeAssortment',
          width: 130,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: true }),
          type: 'numericAlignment',
          columnGroupShow: 'open',
          valueFormatter: params =>
            agGridUtils.formatters.roundFormatter(params, { format: 'number' }),
          valueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, {
              returnAsNumber: true,
              format: 'number',
            }),
          filterValueGetter: params =>
            agGridUtils.formatters.roundFormatter(params, {
              returnAsNumber: true,
              format: 'number',
            }),
        },
      ],
      distributionColumns: [
        {
          headerName: this.tkey('weeksCount'),
          headerTooltip: this.tkey('headerTooltips.weeksCount'),
          field: 'weeksCount',
          colId: 'weeksCount',
          width: 120,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: false }),
          columnGroupShow: 'open',
          type: 'numericAlignment',
        },
        {
          headerName: this.tkey('regionCount'),
          headerTooltip: this.tkey('headerTooltips.regionCount'),
          field: 'regionCount',
          colId: 'regionCount',
          width: 130,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: false }),
          columnGroupShow: 'open',
          type: 'numericAlignment',
        },
        {
          headerName: this.tkey('storeCount'),
          headerTooltip: this.tkey('headerTooltips.storeCount'),
          field: 'storeCount',
          colId: 'storeCount',
          width: 120,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.utils.getNumericFilterParams({ allowNegative: false }),
          columnGroupShow: 'open',
          type: 'numericAlignment',
        },
      ],
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useWorkpackagesStore, ['isFreshWP']),
    ...mapState(useClusteringStore, ['selectedScheme']),
    ...mapState(useContextStore, ['getClientConfig', 'getDateFormats']),
    ...mapState(useScenarioCdtsStore, ['scenarioCdts']),
    ...mapGetters(useScenarioProductsStore, ['consumeOveriewProductOrder']),

    hasDataErrors() {
      return this.doesNotHaveSisProductsSelected;
    },

    hasDataChanges() {
      return !isEmpty(this.currentStateDiff);
    },

    isEditingDisabled() {
      return !this.canEditModellingPage;
    },

    shouldShowOnlyEligibleProducts() {
      return this.currentlySelectedSisterProductsView === 'eligibleOnly';
    },

    isEstablishedProduct() {
      return this.product.modellingType === modellingTypeOptions.modellingTypes.established;
    },

    doesNotHaveSisProductsSelected() {
      const currentSisProductsSelected = merge(
        {},
        pick(this.savedState, ['sisterProducts']),
        pick(this.currentStateDiff, ['sisterProducts'])
      );

      const noSisters =
        size(currentSisProductsSelected) &&
        every(currentSisProductsSelected.sisterProducts, sp => !sp.isSelected);

      if (noSisters) this.showWarning(this.tkey('mustHaveSisterProductsWarning'));

      return noSisters;
    },

    selectedCDT() {
      // state may not be fully initialised
      if (isEmpty(this.scenarioCdts) && !this.product) return {};
      return this.scenarioCdts.find(cdt => cdt.cannGroupId === this.product.cannGroupId);
    },

    cdtAttributesByKey() {
      if (isEmpty(this.selectedCDT)) return [];

      return keyBy(get(this.selectedCDT, 'attributes', {}), 'attributeId');
    },

    productAttributesByKey() {
      if (isEmpty(this.product)) return {};

      return keyBy(get(this.product, 'customAttributes', {}), 'id');
    },

    productAlertParams() {
      const messages = modellingUtils.getProductModellingAlertsTooltip({ product: this.product });
      return {
        messages,
        hide: isEmpty(messages),
        icon: 'mdi-alert-circle-outline',
        color: 'warning',
        size: 'large',
      };
    },

    copyDecisionsParams() {
      return {
        hide: !this.product.hasCopiedModellingDecisions,
        messages: [this.tkey('copiedDecisionsTooltip')],
        icon: 'mdi-import',
        size: 'large',
        color: 'primary',
      };
    },

    fullProductTitle() {
      return `${this.product.productKeyDisplay} - ${this.product.itemDescription} -
            ${get(this.leafCannGroups, `${this.product.cannGroupId}.name`, '')}`;
    },

    displayedProductTitle() {
      return truncate(this.fullProductTitle, { length: 100 });
    },

    hasMarkedAsCompleted() {
      return !!this.product.modellingMarkedComplete;
    },

    orderedAttributesAccordingToCDT() {
      if (isEmpty(this.selectedCDT)) return [];

      const attributesNotSelectedForCDT = reject(this.selectedScenario.customAttributes, attr =>
        this.selectedCDT.attributes.some(cdtAtt => cdtAtt.attributeId === attr.id)
      );
      let selectedAttributesInCDT = this.selectedCDT.attributes;
      let otherAttributesFromCDTUnselected = [];
      const isUserModifiedCDT = this.selectedCDT.type === cdtTypes.userModified;
      if (isUserModifiedCDT) {
        // A user modified may have a mix of selected/unselected attributes as they get deprioritised by user

        [selectedAttributesInCDT, otherAttributesFromCDTUnselected] = partition(
          this.selectedCDT.attributes,
          'isSelected'
        );
      } else {
        selectedAttributesInCDT.forEach(attr => {
          // On tool generated CDT, user may select several attributes, but these can be dropped by the algorithm.
          // So for this type of CDT, we must check if they've been ranked. This is the same check done on the analytics side
          // as analytics/common_tasks/cluster_storeclass_switching_matrices_calculation/calculate_cluster_storeclass_switching_matrices.py#_get_product_attributes_df
          attr.isSelected = attr.rank !== null;
        });
      }
      // we must respect the order of attributes according to the CDT selection, then any other attr in scenario
      return selectedAttributesInCDT
        .concat(otherAttributesFromCDTUnselected)
        .concat(attributesNotSelectedForCDT);
    },

    customAttributesColumns() {
      // state may not be fully initialised
      if (!this.product) return [];

      const attributesInScenario = keyBy(this.selectedScenario.customAttributes, 'id');
      // we must respect the order of attributes according to the CDT selection, then any other attr in scenario
      // however, if the CDT isn't setup, we default to the order of attributes as shown in scenario
      const attributeColumns = (this.orderedAttributesAccordingToCDT || attributesInScenario).map(
        (attr, index) => {
          const attributeId = attr.attributeId || attr.id;
          const borderClass = index === 0 ? 'border-s-thin' : '';
          // not all attributes used for the CDT are selected
          return {
            headerName: attributesInScenario[attributeId].name,
            colId: `customAttributes.${attributeId}`,
            field: `customAttributes.${attributeId}.value`,
            cellClass: borderClass,
            headerClass: borderClass,
            cellClassRules: { 'matching-attribute': this.attributeMatches },
            // hide by default the custom attributes if they're not selected, but still allow user to toggle them back visible
            hide: !attr.isSelected,
          };
        }
      );

      return {
        headerName: this.t('scenarioInputPage.attributes.tableHeaders.customAttributes'),
        colId: 'customAttributes',
        children: attributeColumns,
        headerClass: 'border-s-thin',
      };
    },

    headers() {
      const clusters = get(this.selectedScheme, 'clusters', []);
      const performanceColumns = this.createBasicColumns(
        {
          colId: 'expectedPerformance',
          headerName: this.tkey('expectedPerformance'),
        },
        [
          this.clusterableColumns.rateOfSale,
          this.clusterableColumns.rosPercentile,
          this.clusterableColumns.salesConfidenceScorePerformancePeriod,
          ...(this.isFreshWP ? [this.clusterableColumns.shrinkToSalesRatio] : []),
          this.clusterableColumns.margin,
          this.clusterableColumns.price,
          this.clusterableColumns.totalMarkdownAssortmentPercentage,
          this.clusterableColumns.wastePercentageAssortment,
        ],
        clusters
      );
      const performanceClusteredColumns = this.createClusteredColumns(
        [
          this.clusterableColumns.rateOfSale,
          this.clusterableColumns.salesConfidenceScorePerformancePeriod,
          ...(this.isFreshWP ? [this.clusterableColumns.shrinkToSalesRatio] : []),
          this.clusterableColumns.margin,
          this.clusterableColumns.price,
          this.clusterableColumns.totalMarkdownAssortmentPercentage,
          this.clusterableColumns.wastePercentageAssortment,
        ],
        clusters
      );
      performanceColumns.children.push(...performanceClusteredColumns);

      const historicalPerformanceColumns = this.createBasicColumns(
        {
          colId: 'historicalPerformance',
          headerName: this.tkey('historicalPerformance'),
        },
        this.historicalPerformanceColumns
      );

      const distributionColumns = this.createBasicColumns(
        {
          colId: 'distribution',
          headerName: this.tkey('distribution'),
        },
        this.distributionColumns
      );

      const customAttributeColumns = this.customAttributesColumns;

      const columns = [
        ...this.initialHeaders,
        performanceColumns,
        historicalPerformanceColumns,
        distributionColumns,
      ];
      if (size(customAttributeColumns)) {
        columns.push(customAttributeColumns);
      }

      const extraColumns = cloneDeep(get(this.getClientConfig, 'modellingDetailPage.columns', {}));

      const mergedHeaders = agGridUtils.builders.mergeHeaders(columns, extraColumns, {
        componentName: camelCase(this.$options.name),
        // make the first column visible at all times, everything else is collapsed by default
        firstGroupChildColumnVisible: true,
      });

      // Apply filters to the columns, if they exist.
      return modellingUtils.applyColumnFilters(
        mergedHeaders,
        this.modellingDetailPageAllowedColumns
      );
    },

    isClearFiltersDisabled() {
      return isEmpty(this.filterModel) && !this.searchString;
    },
  },

  async created() {
    await this.init();
  },

  mounted() {
    // Allow keyboard navigation
    // User need to press the keys Alt + ArrowRight or
    // Alt + ArrowLeft in order to navigate using shortcuts.
    // The sortcut was combined with the 'Alt' key in order
    // to prevent AgGrid navigation issues and text manipulations.
    onKeyStroke(['Alt', 'ArrowRight'], e => {
      if (e.code === 'ArrowRight' && e.altKey) {
        this.move(1);
      }
    });
    onKeyStroke(['Alt', 'ArrowLeft'], e => {
      if (e.code === 'ArrowLeft' && e.altKey) {
        this.move(-1);
      }
    });
    onKeyStroke('Escape', () => this.goBack());
  },

  methods: {
    ...mapActions(useScenarioProductsStore, [
      'saveScenarioProducts',
      'saveScenarioDetails',
      'fetchScenarioProductsWithClusters',
      'updateClusteredScenarioProducts',
      'runSetupProductModelling',
    ]),
    ...mapActions(useClusteringStore, ['fetchScenarioClusters']),
    ...mapActions(useScenariosStore, [
      'updateScenarioCannGroups',
      'fetchScenarioCannGroups',
      'fetchScenarios',
      'setScenarioCannGroupsWithNotesMutation',
    ]),
    ...mapActions(useSnackbarStore, ['showWarning', 'showSnackbar']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),

    getColumnStateDetails() {
      const scenarioId = get(this.selectedScenario, '_id', null);

      return {
        namePrefix: DETAIL_COLUMN_STATE_PREFIX,
        // Key differs per cann group as they have different attributes
        stateId: `${scenarioId}-${this.product.cannGroupId}`,
        version: DETAIL_COLUMN_STATE_VERSION,
      };
    },

    /**
     * Saves the current column state to localStorage
     * Filters out custom attribute columns before saving as these should be reset
     * based on CDT selection when the page is revisited
     */
    saveColumnState() {
      const config = this.getColumnStateDetails();
      if (!this.gridApi || !config) return;

      const columnDefs = this.gridApi.getColumnDefs();
      gridPreferences.saveColumnState(config, columnDefs);
    },

    /**
     * Loads the saved column state from localStorage
     * Custom attribute columns are intentionally not included in the saved state
     * so they will be displayed based on the current CDT selection
     */
    loadColumnState() {
      const config = this.getColumnStateDetails();
      if (!this.gridApi || !config) return;

      const savedState = gridPreferences.loadColumnState(config);

      if (!savedState) return;

      const currentState = this.gridApi.getColumnDefs();

      // Merge saved state with current state
      const mergedState = gridPreferences.mergeColumnStates(savedState, currentState);

      // Apply the merged state to the grid
      this.gridApi.setGridOption('columnDefs', mergedState);
    },

    /**
     * Relevance score is how similar the product's CDT values are to the modelled product
     * We use this to order the sister products in terms of similarity
     */
    getRelevanceScore(params) {
      const sisterAttrs = get(params.data, 'customAttributes', []);

      const relevanceScore = reduce(
        sisterAttrs,
        (score, { id, value }) => {
          const productValue = get(this.productAttributesByKey[id], 'value');

          const coefficient = get(this.cdtAttributesByKey[id], 'coefficient', 0);
          const isSelected = get(this.cdtAttributesByKey[id], 'isSelected', false);

          const shouldAddScore = value === productValue && isSelected;

          return score + (shouldAddScore ? coefficient : 0);
        },
        0
      );

      return round(relevanceScore, 2);
    },

    getProductModellingAlertsTooltip(product) {
      return modellingUtils.getProductModellingAlertsTooltip({ product });
    },

    onExport() {
      const timestamp = dayjs().format(this.getDateFormats.mediumWithTimeAndHyphens);

      this.gridApi.exportDataAsExcel({
        fileName: `Product-Modelling-Detail-${timestamp}`,
        allColumns: false,
        exportedRows: 'filteredAndSorted',
        freezeColumns: 'pinned',
        freezeRows: 'headersAndPinnedRows',
        rowGroupExpandState: 'match',
        sheetName: 'Detail',
      });
    },

    setProduct(productKeyDisplay) {
      const [currentProduct, allOtherProducts] = partition(
        this.clusteredProductData,
        p => p.productKeyDisplay === productKeyDisplay
      );

      this.product = currentProduct[0];
      if (!this.product) {
        return this.goBack();
      }

      this.currentStateDiff = {};

      this.savedState = cloneDeep(this.product);
      // could potentially just be a getter?
      const [allEligibleProducts, allIneligibleProducts] = partition(
        allOtherProducts,
        'isEligibleToBeSister'
      );
      const originalSelectedSisterProducts = map(this.product.sisterProducts, 'productKey');
      // Let original selections to be at the top of the table
      this.allEligibleProducts = sortBy(
        allEligibleProducts,
        p => !originalSelectedSisterProducts.includes(p.productKey)
      );
      this.allIneligibleProducts = allIneligibleProducts;
      this.calculateAllFinalRosPercentile(allOtherProducts);

      allOtherProducts.forEach(otherProduct => {
        const isSelected = get(
          this.product,
          `sisterProducts.${otherProduct.productKey}.isSelected`,
          false
          // The ag-grid checkbox must be true or false - undefined shows up as a special icon
          // We set this to false so the UI looks as expected
        );

        otherProduct.isSelectedSisterProduct = isSelected;
        otherProduct.rosPercentile = this.productsRoSPercentileMap[otherProduct.productKey];
      });

      this.gridRowData = sortBy(
        allOtherProducts,
        p => !originalSelectedSisterProducts.includes(p.productKey)
      );

      this.pinnedRows = [cloneDeep(this.product)];

      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', this.headers);
        this.loadColumnState();
        this.applyDefaultSort();
      }
    },

    applyDefaultSort() {
      const columnsWithSortApplied = filter(this.gridApi.getColumnState(), 'sort');
      // We want to avoid overriding their preserved settings, if they exist
      if (size(columnsWithSortApplied)) return;

      this.gridApi.applyColumnState({
        state: [
          {
            colId: 'relevanceScore',
            sort: 'desc',
          },
        ],
      });
    },

    async init() {
      this.isLoading = true;

      const { clusteredProductData, productsWithoutParents } = await this.fetchData();
      this.clusteredProductData = clusteredProductData;

      this.productsRequiringModelling = filter(
        productsWithoutParents,
        p => !this.isProductEstablished(p.modellingType, p.salesConfidenceScorePerformancePeriod)
      );

      this.leafCannGroups = keyBy(
        cannGroupUtils.getLeafNodes(await this.fetchScenarioCannGroups()),
        'key'
      );

      this.setProductOrderFromOverview();

      this.setProduct(this.route.params.productKeyDisplay);
      this.isLoading = false;
    },

    setProductOrderFromOverview() {
      const overviewProductOrder = this.consumeOveriewProductOrder;
      if (isEmpty(overviewProductOrder)) {
        return;
      }

      const orderMap = reduce(
        overviewProductOrder,
        (result, productKeyDisplay, index) => {
          result[productKeyDisplay] = index;
          return result;
        },
        {}
      );

      // Filter out the filtered products
      this.productsRequiringModelling = filter(this.productsRequiringModelling, p =>
        has(orderMap, p.productKeyDisplay)
      );

      // Sort the subset array based on the order in the parent array
      this.productsRequiringModelling = sortBy(
        this.productsRequiringModelling,
        item => orderMap[item.productKeyDisplay]
      );
    },

    discardChanges() {
      // Re-applying the product will reinitialise all state
      this.setProduct(this.route.params.productKeyDisplay);
    },

    async onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.setGridOption('columnDefs', this.headers);
      setGridApiForTesting('productModellingDetail', params.api);
    },

    handleCellEdit(params) {
      let { field } = params.colDef;
      const { productKey } = params.data;
      const sisterProductUpdatePath = `sisterProducts.${productKey}.isSelected`;
      // params.value is displayed value, params.data[params.colDef.field] is value as stored in db
      // e.g. params.value = "5,1", params.data[params.colDef.field] = 5.1
      const currentValue = get(params.data, field);

      let originalValue = get(this.savedState, field);

      if (field === 'isSelectedSisterProduct') {
        // Overwrite field to match the savedState object
        field = sisterProductUpdatePath;
        originalValue = get(this.savedState, field, false);
      }

      if (agGridUtils.comparators.didValueChange(currentValue, originalValue)) {
        if (!this.currentStateDiff) this.currentStateDiff = {};
        // _.set() incorrectly parses numbers as array indices and creates massive sparse arrays
        setWith(this.currentStateDiff, field, currentValue, Object);
      }

      if (currentValue === originalValue && has(this.currentStateDiff, field)) {
        // Remove the field from the object if it's back to its old value.
        // Note: This is only triggered on a change so entering 1 in a cell that already contains 1 triggers nothing.
        this.unsetCurrentStateDataDiff(this.currentStateDiff, field);
        if (isEmpty(this.currentStateDiff)) {
          // If there's nothing left in the object, remove it.
          delete this.currentStateDiff;
        }
      }

      if (field === sisterProductUpdatePath) {
        this.recalculateKPIs();
      }
    },

    getUpsertSisProductsRows() {
      const selectedSisterProducts = merge(
        {},
        this.savedState.sisterProducts,
        this.currentStateDiff.sisterProducts
      );

      const sisterProductUpserts = mapValues(selectedSisterProducts, (sp, pKey) => ({
        // The diff object only has isSelected if it's not tool-suggested. Take the key from the diff
        productKey: get(sp, 'productKey', +pKey),
        // If it's not already marked as isToolGenerated, then it isn't.
        isToolGenerated: get(sp, 'isToolGenerated', false),
        isSelected: get(sp, 'isSelected', false),
      }));

      const oldAndNewSelections = values(sisterProductUpserts);

      const currentlySelectedSisterProductKeys = map(
        filter(oldAndNewSelections, 'isSelected'),
        'productKey'
      );

      return {
        sisterProductUpserts,
        currentlySelectedSisterProductKeys,
      };
    },

    getAllowedModifiedFields() {
      const fieldsToPick = ['rateOfSale'];
      if (this.product.isNewProduct) {
        // Only apply these KPIs if this is a new product. Existing products should use the calculated values.
        fieldsToPick.push(
          ...[
            'totalSales',
            'wasteAndMarkdown',
            'wasteLossAndMarkdown',
            'totalMarkdown',
            'totalMarkdownAssortment',
            'wasteAssortment',
            'wastePercentageAssortment',
            'wasteAndMarkdownPercentage',
            'wasteLossAndMarkdownPercentage',
            'shrinkToSalesRatio',
            'totalMarkdownAssortmentPercentage',
            'waste',
            'wastePercentage',
            'wastePercentageAssortment',
            'margin',
            'price',
          ]
        );
      }
      return fieldsToPick;
    },

    async recalculateKPIs() {
      if (this.doesNotHaveSisProductsSelected) return;

      const { currentlySelectedSisterProductKeys } = this.getUpsertSisProductsRows();

      const updates = [
        {
          productKey: this.product.productKey,
          sisterProductKeys: currentlySelectedSisterProductKeys,
        },
      ];

      this.isGridLoading = true;
      const [err, updatedClusteredRoS = []] = await to(
        jobApi.runFunction('update-products-ros', {
          scenario_id: this.selectedScenario._id,
          updates,
        })
      );
      this.isGridLoading = false;

      if (err) {
        this.showWarning();
      }

      // The updatedClusteredRoS will contain [unclustered + any clustered] entries.
      updatedClusteredRoS.forEach(clusterUpdate => {
        const pinnedRow = this.gridApi.getPinnedTopRow(0);

        const clusterPrefix = isNil(clusterUpdate.clusterId) ? '' : `${clusterUpdate.clusterId}.`;
        const allowedFieldsToModify = this.getAllowedModifiedFields();

        forEach(clusterUpdate, (value, field) => {
          // Update the pinned row with the new value
          if (allowedFieldsToModify.includes(field)) {
            // Neither `setData` nor `updateData` correctly fires an event to @cellValueChanged
            // So the track diff is not updated. So we pass each value as being changed for now.
            pinnedRow.setDataValue(`${clusterPrefix}${field}`, value);
          }
        });
      });
    },

    async clearCachedForecaster() {
      try {
        // Always clear cached forecaster when product modelling changes are saved
        await this.triggerDependencyTree({
          params: {
            change: 'forecasterInputsModified',
            updates: {},
            commit: true,
          },
        });
      } catch (error) {
        console.warn('Failed to clear cached forecaster:', error);
        // Don't throw - this shouldn't block the save operation
      }
    },

    async markAsComplete() {
      const { productKeyDisplay, productKey } = this.product;

      // Get the base product update
      const { sisterProductUpserts } = this.getUpsertSisProductsRows();
      const baseProductUpdateFields = this.getAllowedModifiedFields();
      const baseProductUpdate = pick(this.currentStateDiff, baseProductUpdateFields);

      const update = {
        ...pick(this.product, ['_id']),
        modellingMarkedComplete: true,
        ...baseProductUpdate,
      };

      if (size(sisterProductUpserts)) update.sisterProducts = values(sisterProductUpserts);

      // Get the clustered products updates
      const clusterIds = map(get(this.selectedScheme, 'clusters', []), 'clusterId');
      const clusterDiffUpdates = pick(this.currentStateDiff, clusterIds);

      const clusterScenProdUpserts = map(clusterDiffUpdates, (clusterChanges, clusterId) => ({
        clusterId,
        productKey,
        scenarioId: this.selectedScenario._id,
        clusterSchemeId: this.selectedScheme._id,
        ...pick(clusterChanges, this.getAllowedModifiedFields()),
      }));

      // Save these to the DB and handle errors
      const [[scenProdErr], [clusterProdErr]] = await Promise.all([
        to(this.saveScenarioDetails([update])),
        to(this.updateClusteredScenarioProducts(clusterScenProdUpserts)),
      ]);

      if (scenProdErr || clusterProdErr) {
        this.showSnackbar({
          color: 'red',
          content: this.tkey('actions.saveFailure'),
        });
        throw new Error(scenProdErr || clusterProdErr);
      }

      // We've successfully updated, reset the diff to avoid unsaved data warning.
      this.currentStateDiff = {};
      this.showSnackbar({
        color: 'green',
        content: this.tkey('actions.saveSuccess'),
      });

      // TODO - AOV3-8101 - change this DT to use proceedWithoutDeleteProductModellingModified instead
      // Always clear cached forecaster after successful save
      await this.clearCachedForecaster();

      // The update has completed successfully, modify the local copy to reflect this.
      const modifiedProduct = find(this.clusteredProductData, { productKeyDisplay });
      assign(modifiedProduct, {
        ...update,
        sisterProducts: sisterProductUpserts,
        ...clusterDiffUpdates,
      });
    },

    onViewToggleChange() {
      // Let AG-Grid know we've changed the filter view
      this.gridApi.onFilterChanged();
    },

    canRowBeSelected(node) {
      if (this.isEstablishedProduct) return false;
      // Eligible products to be selected must be from same cann group
      return (
        node.data.isEligibleToBeSister === true &&
        node.data.cannGroupId === this.product.cannGroupId
      );
    },

    doesExternalFilterPass(node) {
      const productKeyDisplay = get(node.data, 'productKeyDisplay');
      const itemDescription = get(node.data, 'itemDescription');

      if (!isEmpty(this.searchString)) {
        return productUtils.searchProductByKeyOrDescription(
          this.searchString,
          productKeyDisplay,
          itemDescription
        );
      }

      if (!this.shouldShowOnlyEligibleProducts) return true;

      return this.canRowBeSelected(node);
    },

    createBasicColumns(parentColumnDefinition, basicColumnsDefinition) {
      // This is unc
      const columnsToAdd = {
        ...parentColumnDefinition,
        children: isArray(basicColumnsDefinition)
          ? basicColumnsDefinition
          : [{ ...basicColumnsDefinition }],
      };

      return columnsToAdd;
    },
    createClusteredColumns(basicColumnsDefinition, clusters = []) {
      const clusterColumns = clusters.map(cluster =>
        this.createBasicColumns(
          {
            headerName: cluster.clusterName,
            columnGroupShow: null,
          },
          [
            ...basicColumnsDefinition.map(basicColumnDefinition => ({
              ...basicColumnDefinition,
              field: `${cluster.clusterId}.${basicColumnDefinition.field}`,
              colId: `${cluster.clusterId}.${basicColumnDefinition.field}`,
            })),
          ]
        )
      );

      return clusterColumns;
    },

    hasDiff(params) {
      const { productKey } = params.data;
      const { field } = params.colDef;

      const isMainProduct = productKey === this.product.productKey;
      const editingSisterProductSelection = field === 'isSelectedSisterProduct';
      const isEditingMainRowSisterProductSelection = isMainProduct && editingSisterProductSelection;

      // hasDiff should only execute if editing the main row or the checkbox column for sisterProducts
      if (
        (!isMainProduct && !editingSisterProductSelection) ||
        isEditingMainRowSisterProductSelection
      ) {
        return false;
      }

      const currentValue = get(params.data, params.colDef.field);
      let originalValue = get(this.savedState, field);

      if (editingSisterProductSelection) {
        const savedSisterProduct = this.savedState.sisterProducts[productKey];
        originalValue = get(savedSisterProduct, 'isSelected', false);
      }
      const isNumericAndCurrentlyZero =
        params.colDef.type === 'numericColumnPermissiveCustom' && currentValue === 0;
      // return false if values are the same or if undefined or null is compared to an empty string or zero for numeric fields
      // Ensures that, when the value was nil, after convertion of numeric field, it is still nill
      const isNilOrEmptyOrZero =
        isNil(originalValue) && (currentValue === '' || isNumericAndCurrentlyZero);
      const didValueChange = agGridUtils.comparators.didValueChange(currentValue, originalValue);

      return didValueChange && !isNilOrEmptyOrZero;
    },

    attributeMatches(params) {
      return params.value === get(this.product, params.colDef.field);
    },

    goBack() {
      if (this.isGridLoading) return;

      this.router.push({
        name: 'product-modelling',
        query: { ...this.route.query },
        params: { ...omit({ ...this.route.params }, ['productKeyDisplay']) },
      });
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    async move(ix) {
      if (this.isGridLoading) return;

      const currentIx = findIndex(this.productsRequiringModelling, {
        productKeyDisplay: this.product.productKeyDisplay,
      });

      // Handle iterating back and forth through the array of modellable products
      const arrLen = this.productsRequiringModelling.length;
      const newIx = (currentIx + ix + arrLen) % arrLen;

      const { productKeyDisplay } = this.productsRequiringModelling[newIx];

      // We get an error if the navigation is blocked. This happens if you opt to stay on page.
      // If there's no error, we can proceed.
      const [, navBlockedErr] = await to(
        this.router.push({
          name: 'product-detail',
          params: { productKeyDisplay },
          query: get(this.route, 'query', {}),
        })
      );

      if (!navBlockedErr) {
        // ag-grid will reuse rows, including styling. This can cause false positive selections when moving between products A -> B
        // where a sister-product was selected for A and still present, though not selected, in B
        this.gridApi.deselectAll();
        this.setProduct(productKeyDisplay);
      }
    },

    async saveAndProceed() {
      await this.markAsComplete();
      this.move(1);
      this.dependencyTreeModalOpen = false;
    },

    async handleSaveAndProceed(commit = false) {
      try {
        if (size(this.currentStateDiff)) {
          const results = await this.triggerDependencyTree({
            params: { change: 'productModellingModified', updates: {}, commit },
          });

          if (results.needsFeedback) {
            this.dependencyTreeFeedback = omit(results.output, ['isAssortmentOnly']);
            this.dependencyTreeModalOpen = true;
            return;
          }
        }

        await this.saveAndProceed();
      } catch (e) {
        console.error(e);
      }
    },

    clearFilters() {
      this.searchString = '';
      this.gridApi.setFilterModel(null);
      this.gridApi.onFilterChanged();
    },

    onFilterChanged() {
      this.gridApi.onFilterChanged();
    },

    calculateAllFinalRosPercentile(allProducts) {
      const len = allProducts.length;
      const sortedData = orderBy(allProducts, 'rateOfSale', 'asc');
      const percentiles = {};

      sortedData.forEach(({ rateOfSale }, i) => {
        const percentile = ((1 + i) / len) * 100;
        percentiles[rateOfSale] = round(percentile, 2);
      });

      // Already map the productKey to the rateOfSale percentile
      allProducts.forEach(product => {
        this.productsRoSPercentileMap[product.productKey] = percentiles[product.rateOfSale];
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
:deep(.matching-attribute) {
  border-bottom: 2px solid $success-colour;
}

.tooltip-text {
  &__message {
    font-size: 1.25rem;
  }
}
</style>
